// Quote Context: Attachments, Files, Emails, History, Other Charges APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  QuoteAttachment, QuoteAttachmentType, QuoteAttachmentUpdateData,
  QuoteContext, OtherCharge,
} from '../quote-api-claims-types'

export const quoteContextApi = {
  // =========================================================================
  // QUOTE ATTACHMENTS
  // =========================================================================

  async listQuoteAttachments(quoteId: string, type?: QuoteAttachmentType): Promise<QuoteAttachment[]> {
    try {
      const client = getApiClient()
      const params: Record<string, string> = {}
      if (type) params.type = type
      const res = await client.get(`/quotebuilder/quotes/${quoteId}/attachments/`, withOrgConfig({ params }))
      return (res.data as { files: QuoteAttachment[] }).files || []
    } catch {
      return []
    }
  },

  async uploadQuoteAttachment(
    quoteId: string,
    file: File,
    options?: { title?: string; description?: string; attachmentType?: QuoteAttachmentType; isPinned?: boolean }
  ): Promise<QuoteAttachment> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)
    if (options?.title) formData.append('title', options.title)
    if (options?.description) formData.append('description', options.description)
    if (options?.attachmentType) formData.append('attachment_type', options.attachmentType)
    if (options?.isPinned !== undefined) formData.append('is_pinned', String(options.isPinned))

    const res = await client.post<QuoteAttachment>(
      `/quotebuilder/quotes/${quoteId}/attachments/`,
      formData,
      withOrgConfig({ headers: { 'Content-Type': 'multipart/form-data' } })
    )
    return res.data
  },

  async getQuoteAttachment(quoteId: string, attachmentId: string): Promise<QuoteAttachment> {
    const client = getApiClient()
    const res = await client.get<QuoteAttachment>(
      `/quotebuilder/quotes/${quoteId}/attachments/${attachmentId}/`,
      withOrgConfig()
    )
    return res.data
  },

  async updateQuoteAttachment(
    quoteId: string,
    attachmentId: string,
    data: QuoteAttachmentUpdateData
  ): Promise<QuoteAttachment> {
    const client = getApiClient()
    const res = await client.patch<QuoteAttachment>(
      `/quotebuilder/quotes/${quoteId}/attachments/${attachmentId}/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async deleteQuoteAttachment(quoteId: string, attachmentId: string): Promise<void> {
    const client = getApiClient()
    await client.delete(
      `/quotebuilder/quotes/${quoteId}/attachments/${attachmentId}/`,
      withOrgConfig()
    )
  },

  async toggleQuoteAttachmentPin(
    quoteId: string,
    attachmentId: string,
    isPinned: boolean,
    pinOrder?: number
  ): Promise<QuoteAttachment> {
    const client = getApiClient()
    const res = await client.post<QuoteAttachment>(
      `/quotebuilder/quotes/${quoteId}/attachments/${attachmentId}/pin/`,
      { is_pinned: isPinned, pin_order: pinOrder || 0 },
      withOrgConfig()
    )
    return res.data
  },

  // =========================================================================
  // LEGACY METHODS (fallback)
  // =========================================================================

  async getQuoteFiles(quoteId: string): Promise<Array<{ id: string; name: string; size: number; type: string; url?: string }>> {
    try {
      const attachments = await quoteContextApi.listQuoteAttachments(quoteId, 'file')
      return attachments.map(a => ({
        id: a.id,
        name: a.title || a.original_filename,
        size: a.file_size,
        type: a.file_type,
        url: a.file_url,
      }))
    } catch {
      return []
    }
  },

  async getQuoteEmails(quoteId: string): Promise<Array<{ id: string; subject: string; from: string; date: string; snippet: string; hasAttachments: boolean }>> {
    try {
      const attachments = await quoteContextApi.listQuoteAttachments(quoteId, 'email')
      return attachments.map(a => ({
        id: a.id,
        subject: a.email_subject || a.title || a.original_filename,
        from: a.email_from || 'Unknown',
        date: a.email_date || a.created_at,
        snippet: a.description || '',
        hasAttachments: false,
      }))
    } catch {
      return []
    }
  },

  async getQuoteContext(quoteId: string): Promise<QuoteContext> {
    try {
      const client = getApiClient()
      const res = await client.get(`/quotebuilder/quotes/${quoteId}/context/`, withOrgConfig())
      return res.data as QuoteContext
    } catch {
      return { quote_id: quoteId, files: [], emails: [], drawings: [], variations: [], claims: { can_generate: false, claims: [], total_claimed: 0 }, history: [] }
    }
  },

  async getPaymentClaims(quoteId: string): Promise<Array<{ id: string; claim_number: number; status: string; claim_amount: number; items_count: number; created_at: string }>> {
    try {
      const client = getApiClient()
      const res = await client.get(`/quotebuilder/quotes/${quoteId}/claims/`, withOrgConfig())
      return res.data as Array<{ id: string; claim_number: number; status: string; claim_amount: number; items_count: number; created_at: string }>
    } catch {
      return []
    }
  },

  async getQuoteHistory(quoteId: string): Promise<Array<{ id: string; action: string; action_type: string; description: string; user_name: string; created_at: string; old_value?: string; new_value?: string }>> {
    try {
      const client = getApiClient()
      const res = await client.get(`/quotebuilder/quotes/${quoteId}/history/`, withOrgConfig())
      return res.data as Array<{ id: string; action: string; action_type: string; description: string; user_name: string; created_at: string; old_value?: string; new_value?: string }>
    } catch {
      return []
    }
  },

  // =========================================================================
  // OTHER CHARGES (Section 4 from Excel)
  // =========================================================================

  async listOtherCharges(quoteId: string): Promise<OtherCharge[]> {
    try {
      const client = getApiClient()
      const res = await client.get<OtherCharge[]>(`/quotebuilder/quotes/${quoteId}/other_charges/`, withOrgConfig())
      return res.data
    } catch {
      return []
    }
  },

  async createOtherCharge(quoteId: string, data: Partial<OtherCharge>): Promise<OtherCharge> {
    const client = getApiClient()
    const res = await client.post<OtherCharge>(
      `/quotebuilder/quotes/${quoteId}/other_charges/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async updateOtherCharge(quoteId: string, chargeId: string, data: Partial<OtherCharge>): Promise<OtherCharge> {
    const client = getApiClient()
    const res = await client.patch<OtherCharge>(
      `/quotebuilder/quotes/${quoteId}/other_charges/${chargeId}/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async deleteOtherCharge(quoteId: string, chargeId: string): Promise<void> {
    const client = getApiClient()
    await client.delete(`/quotebuilder/quotes/${quoteId}/other_charges/${chargeId}/`, withOrgConfig())
  },
}
